import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileText, ExternalLink, CheckCircle2, AlertCircle, ArrowLeft, File, ChevronDown } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import type { Job, DetailedPermitStage, PermitStatus } from "@/data/types";
import type { Attachment } from "@/data/attachmentTypes";
import { FileUploader } from "@/components/ui/file-uploader";
import { toast } from "sonner";
import { PushProgressTracker } from "@/components/PushProgressTracker";
import { NtboTrackerPanel } from "@/components/permitting/NtboTrackerPanel";
import { stageKeyToPushStep, type TrackerStageKey } from "@/lib/pushStageNavigation";
import type { PushNextStep } from "@/components/quick-actions/PushNextStepPanel";
import { derivePreSpendGate, getGatedCtaLabel } from "@/data/preSpendGate";
import { PreSpendGateBanner } from "@/components/PreSpendGateBanner";
import { SettingsService } from "@/services/settings.service";

/**
 * Permit stages that represent COMPANY SPEND. Submitting a permit application
 * incurs municipality fees and starts a non-refundable workflow → must wait
 * for the pre-spend gate to clear (Project Review + Rescission + Financial).
 *
 * Stages BEFORE this list are pre-submission prep (free) and stay open at all times.
 */
const PERMIT_SPEND_STAGES: DetailedPermitStage[] = [
  "Submitted", "Under Review", "Resubmitted", "Approved", "Closed",
];
const isSpendStage = (s: DetailedPermitStage) => PERMIT_SPEND_STAGES.includes(s);

const STAGES: DetailedPermitStage[] = [
  "Not Required",
  "Permit Required",
  "Preparing Documents",
  "Submitted",
  "Under Review",
  "Revision Requested",
  "Resubmitted",
  "Approved",
  "Closed",
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  /** Fired after a successful permit update with the projected updated job — enables next-step continuity. */
  onSuccess?: (updatedJob: Job) => void;
  /** Push-Mode chain: render a "Back" button that returns the operator to the previous step. Hidden once they've made edits. */
  onBack?: () => void;
  /** Push-Mode chain: operator clicked another stage in the progress tracker. */
  onChooseNextStep?: (step: PushNextStep, job: Job) => void;
}

const stageToStatus = (s: DetailedPermitStage): PermitStatus => {
  if (s === "Approved" || s === "Closed") return "Approved";
  if (s === "Revision Requested") return "Revision Needed";
  if (s === "Not Required") return "Not Required";
  if (["Submitted", "Under Review", "Resubmitted"].includes(s)) return "Submitted";
  return "Submitted";
};

const isUuidLike = (v: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.trim());

export function PermitQuickViewModal({ open, onOpenChange, job, onSuccess, onBack, onChooseNextStep }: Props) {
  const { setJobs } = useAppData();
  const [stage, setStage] = useState<DetailedPermitStage>("Preparing Documents");
  const [note, setNote] = useState("");
  const [blockers, setBlockers] = useState("");
  const [coordinator, setCoordinator] = useState("");
  const [dirty, setDirty] = useState(false);
  const [drawingAttachments, setDrawingAttachments] = useState<Attachment[]>([]);
  const [documentAttachments, setDocumentAttachments] = useState<Attachment[]>([]);
  const [feeAttachments, setFeeAttachments] = useState<Attachment[]>([]);
  const profileMapRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    SettingsService.getProfiles().then((profiles) => {
      const map = new Map<string, string>();
      profiles.forEach((p) => {
        const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ");
        if (p.id && name) map.set(p.id, name);
        if (p.auth_user_id && name) map.set(p.auth_user_id, name);
      });
      profileMapRef.current = map;
      // Re-resolve coordinator if the job's raw value is a UUID and profile map
      // wasn't ready when the job effect ran.
      if (job) {
        const rawCoordinator = job.permitDetail?.assignedCoordinator || job.salesRep || "";
        if (isUuidLike(rawCoordinator)) {
          const resolved = map.get(rawCoordinator);
          if (resolved) setCoordinator(resolved);
        }
      }
    }).catch(() => {});
  }, [job]);

  const resolveCoordinatorName = (raw: string) => {
    if (!raw) return "";
    if (isUuidLike(raw)) return profileMapRef.current.get(raw) || "";
    return raw;
  };

  useEffect(() => {
    if (job) {
      setStage((job.permitDetail?.detailedStage as DetailedPermitStage) || "Preparing Documents");
      setNote("");
      setBlockers(job.permitDetail?.blockers || "");
      const rawCoordinator = job.permitDetail?.assignedCoordinator || job.salesRep || "";
      setCoordinator(resolveCoordinatorName(rawCoordinator));
      setDirty(false);
      setDrawingAttachments([]);
      setDocumentAttachments([]);
      setFeeAttachments([]);
    }
  }, [job]);

  // Pre-spend gate must be computed BEFORE the early return for hook stability.
  const preSpendGate = useMemo(() => (job ? derivePreSpendGate(job) : null), [job]);
  const currentStage = (job?.permitDetail?.detailedStage as DetailedPermitStage) || "Preparing Documents";
  const alreadySpending = isSpendStage(currentStage);

  if (!job || !preSpendGate) return null;

  const apply = (nextStage: DetailedPermitStage, actionLabel: string) => {
    // Block: trying to advance INTO a spend stage when gates aren't cleared.
    // Updates that stay within prep stages (or that retreat) remain open.
    if (!preSpendGate.cleared && isSpendStage(nextStage) && !alreadySpending) {
      toast.error("Permit submission blocked", { description: preSpendGate.shortReason });
      return;
    }
    const todayIso = new Date().toISOString().split("T")[0];
    const isSubmitStage = nextStage === "Submitted" || nextStage === "Resubmitted";
    let updated: Job | null = null;
    setJobs((prev) => prev.map((j) => {
      if (j.id !== job.id) return j;
      const prevDetail = j.permitDetail;
      const nextDetail = prevDetail
        ? {
            ...prevDetail,
            detailedStage: nextStage,
            lastUpdate: new Date().toISOString(),
            permitNotes: note || prevDetail.permitNotes,
            submittedDate: isSubmitStage && !prevDetail.submittedDate ? todayIso : prevDetail.submittedDate,
            assignedCoordinator: coordinator || prevDetail.assignedCoordinator,
            blockers: blockers,
          }
        : undefined;
      const next: Job = {
        ...j,
        permitStatus: stageToStatus(nextStage),
        permitDetail: nextDetail,
        activityHistory: [
          ...(j.activityHistory ?? []),
          {
            id: `act-${Date.now()}`,
            date: new Date().toISOString(),
            user: "system",
            action: `${actionLabel}${note ? ` — ${note}` : ""}`,
          },
        ],
      };
      updated = next;
      return next;
    }));
    toast.success(actionLabel, { description: `${job.customerName} · ${nextStage}` });
    onOpenChange(false);
    if (updated && onSuccess) onSuccess(updated);
  };

  const detail = job.permitDetail;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Permit Quick View
          </DialogTitle>
          <DialogDescription>
            {job.customerName} · {job.address?.city || "—"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Push Progress — visible during execution */}
          <PushProgressTracker
            job={job}
            activeKey="permit"
            onStageClick={(key) => {
              if (key === "permit") return;
              onChooseNextStep?.(stageKeyToPushStep(key as TrackerStageKey, job.id), job);
            }}
          />

          {/* Pre-spend gate — submitting a permit application costs the company money. */}
          {!preSpendGate.cleared && !alreadySpending && (
            <PreSpendGateBanner
              gate={preSpendGate}
              context="Permit submission is blocked — application fees and review create company obligation."
            />
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Current Stage</div>
              <Badge variant="outline">{detail?.detailedStage || "—"}</Badge>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Status</div>
              <Badge variant="outline">{job.permitStatus || "—"}</Badge>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Municipality</div>
              <div className="text-sm">{detail?.municipality || job.address?.city || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Required</div>
              <div className="text-sm">{job.permitRequired ? "Yes" : "No"}</div>
            </div>
          </div>

          {(job.permitStatus === "Revision Needed" || detail?.blockers) && (
            <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3 flex gap-2">
              <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <div className="text-xs">
                <div className="font-semibold text-destructive">Action Required</div>
                <div className="text-muted-foreground">
                  {detail?.blockers || detail?.permitNotes || "Permit requires revision before proceeding."}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs">Update Stage</Label>
            <Select value={stage} onValueChange={(v) => { setStage(v as DetailedPermitStage); setDirty(true); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => { setNote(e.target.value); setDirty(true); }}
              placeholder="Add context for this update…"
              className="min-h-[60px] text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Coordinator</Label>
              <input
                type="text"
                value={coordinator}
                onChange={(e) => { setCoordinator(e.target.value); setDirty(true); }}
                placeholder="Assigned rep…"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Blockers</Label>
              <input
                type="text"
                value={blockers}
                onChange={(e) => { setBlockers(e.target.value); setDirty(true); }}
                placeholder="Any blockers…"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>

          {/* Permit Attachments */}
          <div className="space-y-4 border-t border-border/60 pt-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Attachments</p>

            <div className="space-y-1.5">
              <Label className="text-xs">Drawings</Label>
              <FileUploader
                parentType="job"
                parentId={job.id}
                attachmentType="permit"
                acceptedTypes={['image/jpeg', 'image/png', 'application/pdf']}
                maxFiles={5}
                maxSizeMB={20}
                onUploadComplete={(attachments) => setDrawingAttachments((prev) => [...prev, ...attachments])}
                className="[&_.border-dashed]:p-3"
              />
              {drawingAttachments.length > 0 && (
                <div className="space-y-1">
                  {drawingAttachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <File className="h-3 w-3 shrink-0" />
                      <span className="truncate">{a.fileName}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Documents</Label>
              <FileUploader
                parentType="job"
                parentId={job.id}
                attachmentType="document"
                acceptedTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                maxFiles={10}
                maxSizeMB={20}
                onUploadComplete={(attachments) => setDocumentAttachments((prev) => [...prev, ...attachments])}
                className="[&_.border-dashed]:p-3"
              />
              {documentAttachments.length > 0 && (
                <div className="space-y-1">
                  {documentAttachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <File className="h-3 w-3 shrink-0" />
                      <span className="truncate">{a.fileName}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Fees</Label>
              <FileUploader
                parentType="job"
                parentId={job.id}
                attachmentType="invoice"
                acceptedTypes={['application/pdf', 'image/jpeg', 'image/png']}
                maxFiles={5}
                maxSizeMB={10}
                onUploadComplete={(attachments) => setFeeAttachments((prev) => [...prev, ...attachments])}
                className="[&_.border-dashed]:p-3"
              />
              {feeAttachments.length > 0 && (
                <div className="space-y-1">
                  {feeAttachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <File className="h-3 w-3 shrink-0" />
                      <span className="truncate">{a.fileName}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* NTBO Tracker */}
        <details className="group">
          <summary className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground cursor-pointer transition-colors list-none">
            <ChevronDown className="h-3.5 w-3.5 group-open:rotate-180 transition-transform" />
            NTBO Tracker & Permit Prep
          </summary>
          <div className="mt-3">
            <NtboTrackerPanel key={job.id} job={job} readOnly />
          </div>
        </details>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {onBack && !dirty && (
            <Button variant="ghost" size="sm" onClick={onBack} className="mr-auto gap-1.5">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
          )}
          {(!onBack || dirty) && (
            <Link to="/permitting" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-auto">
              <ExternalLink className="h-3 w-3" /> Open Full Permit Tracker
            </Link>
          )}
          <Button variant="outline" size="sm" onClick={() => apply("Revision Requested", "Permit info requested")}>
            Request Info
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => apply(stage, "Permit status updated")}
            disabled={!preSpendGate.cleared && isSpendStage(stage) && !alreadySpending}
            title={!preSpendGate.cleared && isSpendStage(stage) && !alreadySpending ? preSpendGate.shortReason : undefined}
          >
            {!preSpendGate.cleared && isSpendStage(stage) && !alreadySpending
              ? getGatedCtaLabel("Update Status", preSpendGate)
              : "Update Status"}
          </Button>
          <Button
            size="sm"
            onClick={() => apply("Approved", "Permit marked approved")}
            className="gap-1"
            disabled={!preSpendGate.cleared && !alreadySpending}
            title={!preSpendGate.cleared && !alreadySpending ? preSpendGate.shortReason : undefined}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Approved
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
